import React, { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Sheet, SheetContent } from "../ui/sheet";
import { Building2, ChevronDown, Search, X, CheckCircle2 } from "lucide-react";

/**
 * Reusable searchable multi-select for connected businesses / suppliers.
 *
 * Desktop (≥640px): renders a compact inline popover under the trigger.
 * Mobile (<640px):  renders a full-width bottom Sheet with search + scrollable
 *                   list so long business names are always fully visible.
 *
 * Props:
 *   businesses          — array of supplier records with { id, name, phone, linked_owner_id }
 *   selectedIds         — array of currently selected supplier ids
 *   onChange(nextIds[]) — called with the next selectedIds array
 *   testidPrefix        — unique prefix used to disambiguate multiple pickers on the same screen
 *   placeholder         — text shown in the trigger when no ids are selected
 *   compact             — smaller (h-11) trigger for use inside table rows
 */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

export function BusinessMultiSelect({
  businesses = [],
  selectedIds = [],
  onChange,
  testidPrefix = "biz",
  placeholder = "Choose businesses...",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const isMobile = useIsMobile();

  const toggle = (bid) => {
    if (selectedIds.includes(bid)) onChange(selectedIds.filter((x) => x !== bid));
    else onChange([...selectedIds, bid]);
  };
  const clearAll = () => onChange([]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? businesses.filter(
        (b) => (b.name || "").toLowerCase().includes(q) || (b.phone || "").toLowerCase().includes(q)
      )
    : businesses;

  const label =
    selectedIds.length === 0
      ? placeholder
      : `${selectedIds.length} business${selectedIds.length === 1 ? "" : "es"} selected`;

  const Trigger = (
    <button
      type="button"
      onClick={isMobile ? () => setOpen(true) : undefined}
      data-testid={`${testidPrefix}-trigger`}
      className={`w-full flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-2xl ${
        compact ? "h-11 px-3" : "h-12 px-3.5"
      } text-sm font-semibold text-slate-700 hover:border-emerald-300 transition`}
    >
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition ${open ? "rotate-180" : ""}`} />
    </button>
  );

  const List = (
    <div className="max-h-full overflow-y-auto py-1" data-testid={`${testidPrefix}-options`}>
      {filtered.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6">No businesses match</div>
      ) : (
        filtered.map((b) => {
          const on = selectedIds.includes(b.id);
          return (
            <button
              key={b.id}
              onClick={() => toggle(b.id)}
              data-testid={`${testidPrefix}-option-${b.id}`}
              data-selected={on ? "true" : "false"}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-emerald-50 transition ${
                on ? "bg-emerald-50" : ""
              }`}
            >
              <div
                className={`w-5 h-5 rounded-md border-2 grid place-items-center shrink-0 ${
                  on
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-slate-300 text-transparent"
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.6} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{b.name}</div>
                {b.phone && <div className="text-[11px] text-slate-500 truncate">{b.phone}</div>}
              </div>
              {b.linked_owner_id && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded shrink-0">
                  On app
                </span>
              )}
            </button>
          );
        })
      )}
    </div>
  );

  const SearchBox = (
    <div className="flex items-center gap-2 border-b border-slate-100 px-3 h-11 shrink-0">
      <Search className="w-4 h-4 text-slate-400 shrink-0" />
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search businesses..."
        data-testid={`${testidPrefix}-search`}
        className="w-full h-full outline-none text-sm bg-transparent placeholder:text-slate-400"
      />
      {query && (
        <button
          onClick={() => setQuery("")}
          className="text-slate-400 hover:text-slate-600"
          data-testid={`${testidPrefix}-search-clear`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  const FooterBar = (
    <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50 shrink-0">
      <button
        onClick={clearAll}
        disabled={selectedIds.length === 0}
        data-testid={`${testidPrefix}-clear-all`}
        className="text-xs font-semibold text-slate-600 disabled:opacity-40 hover:text-slate-900"
      >
        Clear all
      </button>
      <button
        onClick={() => setOpen(false)}
        data-testid={`${testidPrefix}-done`}
        className="text-xs font-bold text-emerald-700 hover:text-emerald-800"
      >
        Done ({selectedIds.length})
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {Trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            data-testid={`${testidPrefix}-sheet`}
            className="p-0 rounded-t-3xl h-[55vh] max-h-[55vh] flex flex-col gap-0"
          >
            <div className="px-4 pt-3 pb-2 shrink-0 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Select</div>
                <div className="text-base font-bold text-slate-900">Choose Business</div>
              </div>
              <div className="w-9" aria-hidden="true" />
            </div>
            {SearchBox}
            <div className="flex-1 overflow-y-auto min-h-0">
              {List}
            </div>
            {FooterBar}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  // Desktop — original popover behaviour
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{Trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-2xl overflow-hidden"
        data-testid={`${testidPrefix}-popover`}
      >
        {SearchBox}
        {List}
        {FooterBar}
      </PopoverContent>
    </Popover>
  );
}
